import React, { useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useFormikContext } from "formik";
import { ArrowLeft, ArrowRight, Images } from "lucide-react";
import OrganizationRail from "./OrganizationRail";

const buildTabs = ({ basePath, mode }) => {
  const tabs = [
    { key: "general", label: "General", to: `${basePath}/general` },
    { key: "advance", label: "Advanced", to: `${basePath}/advance` },
  ];
  if (mode === "create") {
    tabs.push({
      key: "bulk-upload",
      label: "Bulk Upload",
      to: `${basePath}/bulk-upload`,
    });
  }
  if (mode === "edit") {
    tabs.push({ key: "history", label: "History", to: `${basePath}/history` });
  }
  return tabs;
};

const trailingSegment = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  return parts[parts.length - 1] || "general";
};

const ProductFormShell = ({
  mode,
  title,
  basePath,
  loading = false,
  cancelPath = "/product",
  outletContext,
  submitLabel,
  onImportImages,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const formik = useFormikContext();

  const tabs = useMemo(() => buildTabs({ basePath, mode }), [basePath, mode]);
  const active = trailingSegment(location.pathname);
  const showRail = active === "general";

  const isSubmitting = loading || formik.isSubmitting;

  const goToAdvance = () => navigate(`${basePath}/advance`);
  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const allTouched = Object.keys(errors).reduce((acc, k) => {
        acc[k] = true;
        return acc;
      }, {});
      formik.setTouched(allTouched, false);
      navigate(`${basePath}/general`);
      return;
    }
    formik.submitForm();
  };

  return (
    <div className="pf-shell">
      <header className="pf-topbar">
        <button
          type="button"
          className="pf-topbar__back"
          onClick={() => navigate(cancelPath)}
          aria-label="Back to products"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="pf-topbar__title">
          <span className="pf-topbar__eyebrow">Catalogue</span>
          <span className="pf-topbar__heading">{title}</span>
        </div>

        <div style={{ marginLeft: 24 }}>
          <nav className="pf-segnav" aria-label="Product form sections">
            {tabs.map((t) => (
              <NavLink
                key={t.key}
                to={t.to}
                end
                className={({ isActive }) =>
                  `pf-segnav__item ${isActive ? "is-active" : ""}`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="pf-topbar__spacer" />

        <div className="pf-topbar__actions">
          {onImportImages && (
            <button
              type="button"
              className="pf-btn pf-btn--ghost"
              onClick={onImportImages}
              disabled={isSubmitting}
            >
              <Images size={16} />
              Import images
            </button>
          )}
          <button
            type="button"
            className="pf-btn pf-btn--ghost"
            onClick={() => navigate(cancelPath)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          {active === "general" && (
            <button
              type="button"
              className="pf-btn pf-btn--primary"
              onClick={goToAdvance}
              disabled={isSubmitting}
            >
              Next
              <ArrowRight size={16} />
            </button>
          )}
          {active === "advance" && (
            <button
              type="button"
              className="pf-btn pf-btn--primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="pf-spinner" aria-hidden="true" />
                  Saving
                </>
              ) : (
                submitLabel ?? (mode === "edit" ? "Save Changes" : "Publish Product")
              )}
            </button>
          )}
        </div>
      </header>

      <div className={`pf-layout ${showRail ? "" : "pf-layout--narrow"}`}>
        <main className="pf-layout__main">
          <Outlet context={outletContext} />
        </main>
        {showRail ? (
          <div className="pf-layout__rail">
            <OrganizationRail mode={mode} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProductFormShell;
